/**
 * The cloud stamps rows as `2026-09-04 09:11:54+00`. That two-digit offset is
 * not valid ISO-8601 and `Date.parse` returns NaN for it — every row becomes
 * Invalid Date and the page renders empty with no error. Normalise first.
 */
const TS =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i

/** Epoch ms for a cloud timestamp, or null if it isn't one. */
export function parseTs(value: unknown): number | null {
  const m = TS.exec(String(value ?? '').trim())
  if (!m) return null
  let off = m[3] || ''
  if (off && off.toUpperCase() !== 'Z') {
    const digits = off.slice(1).replace(':', '')
    off =
      off[0] +
      (digits.length <= 2
        ? ('0' + digits).slice(-2) + ':00'
        : digits.slice(0, 2) + ':' + (digits.slice(2) + '00').slice(0, 2))
  }
  const t = Date.parse(`${m[1]}T${m[2]}${off}`)
  return Number.isNaN(t) ? null : t
}

const pad = (n: number) => String(n).padStart(2, '0')

/** `YYYY-MM-DD HH:mm:ss` in UTC — the only format log_search accepts safely. */
export function toCloudStamp(d: Date): string {
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
  )
}

export interface Window {
  from: string
  to: string
  fromMs: number
  toMs: number
}

/**
 * The last `days` days ending now. `to_date` is *now*, never a bare date:
 * a bare `YYYY-MM-DD` means midnight upstream, so today's rows vanish.
 */
export function lastDaysWindow(days: number, now: Date = new Date()): Window {
  const toMs = now.getTime()
  const start = new Date(toMs)
  start.setUTCDate(start.getUTCDate() - (days - 1))
  start.setUTCHours(0, 0, 0, 0)
  return {
    from: toCloudStamp(start),
    to: toCloudStamp(new Date(toMs)),
    fromMs: start.getTime(),
    toMs,
  }
}

/** An explicit `YYYY-MM-DD`..`YYYY-MM-DD` range, inclusive of the end day. */
export function explicitWindow(fromDate: string, toDate: string): Window {
  const start = new Date(`${fromDate}T00:00:00Z`)
  const end = new Date(`${toDate}T23:59:59Z`)
  return {
    from: toCloudStamp(start),
    to: toCloudStamp(end),
    fromMs: start.getTime(),
    toMs: end.getTime(),
  }
}

export type Bucket = 'hour' | 'day' | 'week' | 'month' | 'year'

/** Start-of-bucket epoch ms, UTC. Weeks start Monday. */
export function bucketStart(ms: number, bucket: Bucket): number {
  const d = new Date(ms)
  switch (bucket) {
    case 'hour':
      d.setUTCMinutes(0, 0, 0)
      break
    case 'day':
      d.setUTCHours(0, 0, 0, 0)
      break
    case 'week': {
      d.setUTCHours(0, 0, 0, 0)
      const dow = (d.getUTCDay() + 6) % 7 // Monday = 0
      d.setUTCDate(d.getUTCDate() - dow)
      break
    }
    case 'month':
      d.setUTCHours(0, 0, 0, 0)
      d.setUTCDate(1)
      break
    case 'year':
      d.setUTCHours(0, 0, 0, 0)
      d.setUTCMonth(0, 1)
      break
  }
  return d.getTime()
}

/** Every bucket start in [fromMs, toMs], so empty buckets still get a slot. */
export function bucketRange(fromMs: number, toMs: number, bucket: Bucket): number[] {
  const out: number[] = []
  let cur = bucketStart(fromMs, bucket)
  const end = bucketStart(toMs, bucket)
  let guard = 0
  while (cur <= end && guard++ < 20000) {
    out.push(cur)
    const d = new Date(cur)
    switch (bucket) {
      case 'hour': d.setUTCHours(d.getUTCHours() + 1); break
      case 'day': d.setUTCDate(d.getUTCDate() + 1); break
      case 'week': d.setUTCDate(d.getUTCDate() + 7); break
      case 'month': d.setUTCMonth(d.getUTCMonth() + 1); break
      case 'year': d.setUTCFullYear(d.getUTCFullYear() + 1); break
    }
    cur = d.getTime()
  }
  return out
}

const LABEL: Record<Bucket, Intl.DateTimeFormatOptions> = {
  hour: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' },
  day: { month: 'short', day: 'numeric', timeZone: 'UTC' },
  week: { month: 'short', day: 'numeric', timeZone: 'UTC' },
  month: { month: 'short', year: 'numeric', timeZone: 'UTC' },
  year: { year: 'numeric', timeZone: 'UTC' },
}

export function bucketLabel(ms: number, bucket: Bucket): string {
  return new Intl.DateTimeFormat('en-GB', LABEL[bucket]).format(new Date(ms))
}

export function fullStamp(ms: number): string {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: 'UTC',
  }).format(new Date(ms))
}
